import React, { useState, useMemo, useCallback, useRef, createRef } from 'react'

import { GroupBase, SelectInstance } from 'chakra-react-select'
import { FormikProps } from 'formik'

import {
  Stack,
  Input,
  Box,
  Button,
  Grid,
  GridItem,
  Select,
  Form,
} from '@pnld/components-web'

import { IDataFilterProps, TFilter } from './types'

export * from './types'

const DataFilter: React.FC<IDataFilterProps> = ({
  id,
  filters = [],
  onChangeFilter,
  onSearch,
  hasSearch = true,
  hasDate = true,
  filtersPerRow = 4,
}: IDataFilterProps) => {
  const formRef = useRef<FormikProps<Record<string, string | null>> | null>(
    null
  )
  const selectRefs = useRef<{
    [key: string]: React.RefObject<
      SelectInstance<unknown, false, GroupBase<unknown>>
    >
  }>({})

  const [showFilters, setShowFilters] = useState(false)

  const handleChangeShowFilters = () => {
    setShowFilters(!showFilters)
  }

  const initialValues = useMemo(() => {
    const dynamic = filters.reduce((acc, filter) => {
      if (filter.type === 'select') {
        return { ...acc, [filter.name]: null }
      }

      return { ...acc, [filter.name]: '' }
    }, {} as TFilter)

    return {
      ...(hasSearch ? { search: '' } : {}),
      ...(hasDate ? { startDate: '', finalDate: '' } : {}),
      ...dynamic,
    }
  }, [filters, hasSearch, hasDate])

  const handleClearFilters = useCallback(() => {
    formRef.current?.resetForm({ values: initialValues })
    filters.forEach(filter => {
      if (filter.type === 'select') {
        selectRefs.current[filter.name]?.current?.clearValue?.()
      }
    })

    onChangeFilter?.(initialValues as TFilter)
    onSearch?.(initialValues as TFilter)
  }, [initialValues, onChangeFilter, filters])

  const baseId = id ? `data-filter-${id}` : 'data-filter'

  return (
    <Form
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={(values: Record<string, string | null>) => {
        onChangeFilter?.(values as TFilter)
      }}
      submitWhenChange={!!onSearch}
      enableReinitialize
    >
      <Stack id={baseId} width="100%" spacing={2}>
        <Stack
          direction={{
            base: 'column',
            xl: 'row',
          }}
          spacing={2}
          alignItems="center"
        >
          {hasSearch && (
            <Input
              id={`${baseId}__search`}
              name="search"
              placeholder="Pesquisa"
              iconName="magnifying-glass"
            />
          )}

          <Stack
            direction={{
              base: 'column',
              lg: 'row',
            }}
            spacing={2}
            width={{
              base: '100%',
              xl: 'auto',
            }}
          >
            {hasDate && (
              <>
                <Input
                  id={`${baseId}__start-date`}
                  name="startDate"
                  mask="99/99/9999"
                  placeholder="Data Inicial"
                  iconName="fa-calendar-alt"
                  width={{
                    base: 'full',
                    xl: 160,
                  }}
                />
                <Input
                  id={`${baseId}__end-date`}
                  name="finalDate"
                  mask="99/99/9999"
                  placeholder="Data Final"
                  iconName="fa-calendar-alt"
                  width={{
                    base: 'full',
                    xl: 160,
                  }}
                />
              </>
            )}
            <Stack
              direction={{
                base: 'column',
                lg: 'row',
              }}
              spacing={2}
            >
              {!!filters.length && (
                <Box width={{ base: 'full', lg: 'auto' }}>
                  <Button
                    variant="secondary"
                    label="Mais filtros"
                    border="square"
                    onClick={handleChangeShowFilters}
                    width={{
                      base: 'full',
                      lg: 'auto',
                    }}
                  />
                </Box>
              )}
              <Box width={{ base: 'full', lg: 'auto' }}>
                <Button
                  variant="highlight"
                  label="Limpar filtros"
                  iconName="filter-circle-xmark"
                  border="square"
                  onClick={handleClearFilters}
                  width={{
                    base: 'full',
                    lg: 'auto',
                  }}
                />
              </Box>
              <Box width={{ base: 'full', lg: 'auto' }}>
                <Button
                  id={`${baseId}__apply`}
                  type={onSearch ? 'button' : 'submit'}
                  label="Filtrar"
                  iconName="filter"
                  border="square"
                  width={{
                    base: 'full',
                    lg: 'auto',
                  }}
                  onClick={() => onSearch?.()}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>

        {showFilters && !!filters.length && (
          <Grid
            rowGap="2"
            columnGap="2"
            templateColumns={`repeat(auto-fit, minmax(calc(${
              100 / filtersPerRow
            }% - 0.5rem), 1fr))`}
          >
            {filters.map(filter => {
              if (
                filter.type === 'select' &&
                !selectRefs.current[filter.name]
              ) {
                selectRefs.current[filter.name] =
                  createRef<
                    SelectInstance<unknown, false, GroupBase<unknown>>
                  >()
              }
              return (
                <GridItem key={`filter-${filter.name}`}>
                  {filter.type === 'text' && (
                    <Input
                      id={`${baseId}__${filter.name}`}
                      name={filter.name}
                      label={filter.label}
                      placeholder={filter.placeholder}
                      color="gray.500"
                      _placeholder={{ color: 'gray.500' }}
                      backgroundColor="brand.neutral.light_2"
                      isDisabled={filter.isDisabled}
                    />
                  )}
                  {filter.type === 'select' && (
                    <Select
                      id={`${baseId}__${filter.name}`}
                      selectRef={selectRefs.current[filter.name]}
                      name={filter.name}
                      label={filter.label}
                      placeholder={filter.placeholder}
                      options={filter.options || []}
                      isClearable
                      disabled={filter.isDisabled}
                      isLoading={filter.isLoading}
                    />
                  )}
                </GridItem>
              )
            })}
          </Grid>
        )}
      </Stack>
    </Form>
  )
}

export default DataFilter
